import { AnimatePresence, motion } from "motion/react";
import { CSSProperties } from "react";
import clsx from "clsx";
import useAppSelector from "@/core/helpers/useAppSelector";
import useSetLayer from "@/core/helpers/useSetLayer";
import { Layer } from "@/domains/gasStation/constants/layer";
import { IS_DEV } from "@/domains/main/constants/other";
import {
  selectedFuelAmountSelector,
  selectedProductIdSelector,
  stationSelector,
} from "@/domains/gasStation/slice";
import { brandColor } from "@/domains/gasStation/helpers/brandColor";
import { hexToRgb } from "@/domains/gasStation/helpers/hexToRgb";
import useHandlers from "@/domains/gasStation/hooks/useHandlers";
import useShowGasStation from "@/domains/gasStation/hooks/useShowGasStation";
import GasStationFormContainer from "@/domains/gasStation/containers/GasStationFormContainer";
import GasStationHeaderContainer from "@/domains/gasStation/containers/GasStationHeaderContainer";
import GasStationLocationContainer from "@/domains/gasStation/containers/GasStationLocationContainer";
import GasStationRefuelingContainer from "@/domains/gasStation/containers/GasStationRefuelingContainer";
import GasStationVehicleInfoContainer from "@/domains/gasStation/containers/GasStationVehicleInfoContainer.tsx";
import GasStationButtons from "@/domains/gasStation/components/GasStationButtons";

export default function GasStationContainer() {
  useSetLayer(Layer.GasStation);
  const { isShow, close } = useShowGasStation();
  const gasStation = useAppSelector(stationSelector);
  const { payHandler, isSubmitting } = useHandlers();
  const selectedFuelAmount = useAppSelector(selectedFuelAmountSelector);
  const productId = useAppSelector(selectedProductIdSelector);

  const show = isShow || IS_DEV;

  if (!gasStation) {
    return null;
  }

  return (
    <div className="h-full fixed inset-0 z-window px-[--boundary-offset-x] py-[--boundary-offset-y] flex flex-col justify-center items-end">
      <AnimatePresence>
        {show ? (
          <motion.div
            initial={{ opacity: 0, translateX: "100%" }}
            animate={{ opacity: 1, translateX: "none" }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
            }}
            exit={{ opacity: 0, translateX: "100%" }}
            className="h-full flex flex-col gap-5 w-[25rem] rounded-[1rem] overflow-clip card p-0"
            style={
              {
                "--brand-color": brandColor[gasStation.brand],
                "--brand-color-rgb": hexToRgb(brandColor[gasStation.brand]),
                maxHeight: "calc(100vh - 2 * (var(--boundary-offset-y)))",
              } as CSSProperties
            }
          >
            <GasStationHeaderContainer />
            <div className="flex flex-col gap-5 px-7.5">
              <GasStationLocationContainer />
              <GasStationVehicleInfoContainer />
            </div>
            <div className="relative flex flex-col grow gap-5">
              <div className="relative grow">
                <div
                  className={clsx(
                    "flex flex-col gap-5",
                    "absolute top-0 bottom-0 left-0 right-0 overflow-y-auto px-7.5",
                    "[--scrollbar-my:0.875rem] [--scrollbar-track-bg:#130F144D] [--scrollbar-thumb-bg:var(--brand-color)] [scrollbar-gutter:stable]",
                  )}
                >
                  <GasStationFormContainer className="grow" />
                </div>
              </div>
              <GasStationButtons
                className="px-7.5 pb-7.5"
                disabled={selectedFuelAmount === 0}
                onSubmit={() => payHandler(productId)}
                onCancel={() => {
                  close();
                }}
                isSubmitting={isSubmitting}
              />
              <GasStationRefuelingContainer onClose={close} />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
